import { useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AttendanceLocal } from "../../../../../../../../../../../@types/attendance-local";
import { useUserList } from "../../../../../../../../../../../services/api-tansquery/attendance-local-users";
import { AttendanceLocalUser } from "../../../../../../../../../../../@types/attendance-local-user";
import { usePermission } from "../../../../../../../../../../../hooks/usePermission";
import { useDebouncedValue } from "../../../../../../../../../../../hooks/useDebounce";

type Props = {
    slug: AttendanceLocal["slug_unidade"];
    field: ControllerRenderProps<{
        usuario?: {
            first_name?: string;
            id?: number;
            last_name?: string;
            username?: string;
        };
        setor_data?: {
            setor?: {
                id?: number;
                nome?: string;
                sigla?: string;
            };
        };
        misto?: boolean;
    }, "usuario">
    fieldState: ControllerFieldState
    existingUsers: AttendanceLocalUser[];
    style?: React.CSSProperties;
}

export default function UserField({ slug, field, fieldState, existingUsers, style }: Props) {
    const { isAboveCoordenator } = usePermission();
    const [search, setSearch] = useState<string>();
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data: users, isLoading } = useUserList({
        page: 1,
        search: debouncedSearch,
        unidade: !isAboveCoordenator ? slug : undefined,
    });

    const filteredUsers = useMemo(() => {
        return (users ?? []).filter((user) => {
            const isUserAlreadyAdded = existingUsers.some(
                (existingUser) => existingUser.user.username === user.username
            );
            return !isUserAlreadyAdded;
        });
    }, [users, existingUsers]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value.trim() === "" ? undefined : value);
        }
    }

    return (
        <Autocomplete
            style={style}
            freeSolo={false}
            options={filteredUsers || []}
            value={field.value}
            getOptionLabel={(option) => `${option.first_name} - ${option.username}` || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={(_, newValue) => field.onChange(newValue)}
            onInputChange={handleInputChange}
            loading={isLoading}
            loadingText="Carregando usuários..."
            noOptionsText="Nenhum usuário encontrado"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Responsável"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Selecione um usuário"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
